import { Icon } from '#components/Icon/Icon.js';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useState } from 'react';
import { BlockFormatDropdown } from '../components/BlockFormatDropdown';
import { BlockType } from '../types';
import { ToolbarButton } from './ToolbarButton';
import styles from './ToolbarPlugin.module.css';
import InsertLinkModal from '../modals/InsertLinkModal';

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState<BlockType>('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));

      const anchorNode = selection.anchor.getNode();
      let element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);

      if (elementDOM !== null) {
        if ($isHeadingNode(element)) {
          const tag = element.getTag();
          if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
            setBlockType(tag);
          } else {
            setBlockType('paragraph');
          }
        } else {
          setBlockType('paragraph');
        }
      }

      const parent = anchorNode.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(anchorNode));
    }
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        1,
      ),
    );
  }, [editor, updateToolbar]);

  function addHyphen() {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        selection.insertText('—');
      }
    });
  }

  return (
    <>
      <div className={styles.toolbar}>
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          isActive={isBold}
          ariaLabel='Format Bold'
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          isActive={isItalic}
          ariaLabel='Format Italic'
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')
          }
          isActive={isUnderline}
          ariaLabel='Format Underline'
        >
          <u>U</u>
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')
          }
          isActive={isStrikethrough}
          ariaLabel='Format Strikethrough'
        >
          <s>S</s>
        </ToolbarButton>
        <div className={styles.divider} />
        <BlockFormatDropdown blockType={blockType} />
        <div className={styles.divider} />
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
          }
          ariaLabel='Unordered List'
        >
          <Icon name='unorderedListFilled' size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() =>
            editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
          }
          ariaLabel='Ordered List'
        >
          <Icon name='orderedListFilled' size={16} />
        </ToolbarButton>
        <div className={styles.divider} />
        <ToolbarButton
          onClick={() => setIsLinkModalOpen(true)}
          isActive={isLink}
          ariaLabel='Insert Link'
        >
          <Icon name='link' size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={addHyphen} ariaLabel='Insert Hyphen'>
          {'—'}
        </ToolbarButton>
      </div>
      <InsertLinkModal
        isOpen={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
      />
    </>
  );
}
